import { ReactNode, FC } from 'react';
import { useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { IPartner } from '@/modules/partner/model';
import { PayoutsInfoForm } from '@/modules/partner/model/payoutsInfoForm';
import { getPartner } from '@/modules/partner/store/selectors';
import { useGBTaxProfile } from '@/modules/taxProfile/hooks/useGBTaxProfile';
import { ISubmitFormResult } from '@/shared/models/formSubmit';
import { BottomButtons } from '@/shared/view/BottomButtons';
import { useForm } from '@/shared/view/Form';
import { Section } from '@/shared/view/Section';
import { Button } from '@src-common/view/adapters/Button';
import { SelectField } from '@src-common/view/adapters/reactHookForm/SelectField';
import { TextField } from '@src-common/view/adapters/reactHookForm/TextField';

const mapPartnerToForm = ({ taxProfile, vatRegNo }: IPartner): PayoutsInfoForm => ({
  taxProfile,
  vatRegNo,
});

type PayoutsFormProps = {
  textSubmitButton?: ReactNode;
  onSubmit: (formValues: PayoutsInfoForm) => Promise<ISubmitFormResult<PayoutsInfoForm>>;
};

const PayoutsInfoEditForm: FC<PayoutsFormProps> = ({ textSubmitButton, onSubmit }) => {
  const partner = useSelector(getPartner);
  const intl = useIntl();

  const { handleSubmit, form } = useForm({
    onSubmit,
    defaultValues: mapPartnerToForm(partner),
  });

  const {
    control,
    formState: { isSubmitting },
  } = form;

  const selectedTaxProfile = useWatch({ control, name: 'taxProfile' });
  const { taxProfileOptions, vatValidationRules, vatDescription, isVatRequired } =
    useGBTaxProfile(selectedTaxProfile);

  return (
    <form onSubmit={handleSubmit}>
      <Section>
        <SelectField
          control={control}
          name="taxProfile"
          title={intl.formatMessage({
            defaultMessage: 'Tax Profile',
            id: 'tax_profile_field_title',
            description: 'TAX PROFILE field title',
          })}
          items={taxProfileOptions}
          rules={{
            required: intl.formatMessage({
              defaultMessage: 'Tax profile is required',
              id: 'error_tax_profile_required',
              description: 'Tax profile required error message',
            }),
          }}
        />

        {isVatRequired && (
          <TextField
            name="vatRegNo"
            control={control}
            title={intl.formatMessage({
              defaultMessage: 'VAT REG NO',
              id: '9u8nc2',
              description: 'VAT REG NO field title',
            })}
            rules={vatValidationRules}
            description={vatDescription}
          />
        )}
      </Section>
      <BottomButtons>
        <Button type="submit" isPending={isSubmitting}>
          {textSubmitButton || (
            <FormattedMessage defaultMessage="Save" id="RT8KNi" description="Save button text" />
          )}
        </Button>
      </BottomButtons>
    </form>
  );
};

export { PayoutsInfoEditForm };
